<!--
  This component is made primarily for TableGridContent component. In that component we use this component to make a grid view (table/grid view switch logic).
  It can be used as a standalone component also, but be carefully to provide necessary props to make this component to work.
-->
<template>
  <div
    class="grid-view"
  >
    <template
      v-if="totalRowsCount"
    >
      <!-- Stand in for the rows above and below the window, so the scrollbar is the
           length the whole grid would make it and the drawn rows sit where they
           belong. See `renderedRows`. -->
      <div
        v-if="topSpacerHeight"
        class="grid-view-spacer"
        :style="{ height: `${topSpacerHeight}px` }"
      />

      <!-- Plain elements rather than `v-row` and `v-col`: their every measurement -
           the margins, the padding, the column widths - was already overridden by the
           styles below, so all they added was two Vuetify components per card. -->
      <div
        v-for="row in renderedRows"
        :key="row.index"
        class="grid-view-row"
        :style="rowAlignStyle"
      >
        <div
          v-for="entry in row.items"
          :key="entry.index"
          class="grid-view-column"
          :class="[row.isShort ? 'no-grow' : '']"
        >
          <component
            :is="blockComponent"
            v-if="blockComponent"
            :data="data"
            :full-data="fullData"
            :item="entry.item"
            :item-index="entry.index"
            :main-config="mainConfig"
            :grid-config="config"
            :block-config="blockConfig"
            @expansion-change="onBlockExpansionChange(entry.index, $event)"
          />
        </div>
      </div>

      <div
        v-if="bottomSpacerHeight"
        class="grid-view-spacer"
        :style="{ height: `${bottomSpacerHeight}px` }"
      />
    </template>
    <template
      v-else
    >
      <div
        class="grid-view-no-data"
      >
        {{ $t('base.no_data') }}
      </div>
    </template>
  </div>
</template>

<script>
import helperMixin from '@/global/mixins/helperMixin'

const DEFAULT_COLUMNS_PER_ROW = 4
const DEFAULT_COLUMNS_PER_ROW_MOBILE = 1
const DEFAULT_BLOCKS_ROW_ALIGN = 'start'
// How a row lines its cards up, named the way the grid config names it.
const ROW_ALIGNMENTS = Object.freeze({
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  baseline: 'baseline',
  stretch: 'stretch'
})
// From how many rows of cards the grid is drawn through a window rather than whole.
//
// Counted in rows rather than in records, because a row is what the window is measured
// in - and on mobile a row is a single card, where a couple of screenfuls is already
// more than the browser should be asked to keep laid out. Below the threshold nothing
// changes: the grid is drawn the way it always was.
const VIRTUALIZE_FROM_ROWS = 30
// The fewest rows kept drawn beyond each edge of the window, so a scroll finds them
// already there. Normally a whole screenful either way - see `rowOverscan`.
const MIN_ROW_OVERSCAN = 3
// What a row of cards is assumed to be tall until one has been drawn and measured.
const DEFAULT_ROW_HEIGHT = 220
// How many drawn rows are measured against each other before the window is trusted,
// and how far apart two of them may be and still count as the same height.
const ROW_HEIGHT_SAMPLE = 5
const ROW_HEIGHT_TOLERANCE = 1
// The scroll container to window against - the same one the table windows against, so
// switching between the two views does not change what is scrolling.
const SCROLL_CONTAINER_SELECTOR = '.table-grid-content-wrapper'
const SCROLLABLE_OVERFLOW = ['auto', 'scroll', 'overlay']

export default {
  name: 'GridView',

  mixins: [
    helperMixin
  ],

  props: {
    // table data
    data: {
      type: Array,
      default: () => ([])
    },
    fullData: {
      type: Object,
      default: () => ({})
    },
    // grid block config
    config: {
      type: Object,
      required: true,
      default: () => ({})
    },
    // main table config
    mainConfig: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },

  data () {
    return {
      // Where the scroll container stands and how tall it is - together they say which
      // rows are worth drawing. See `renderedRows`.
      scrollTop: 0,
      viewportHeight: typeof window === 'undefined' ? 0 : window.innerHeight,
      measuredRowHeight: 0,
      scrollContainer: null,
      // Whether it has been looked for yet. Until it has, a long grid is windowed on
      // the assumption that there is one - the alternative is drawing every card on the
      // first pass, which is the thing being avoided.
      isScrollContainerResolved: false,
      scrollFrameId: null,
      resizeObserver: null,
      // Whether the rows turned out to be the same height as each other - see
      // `measureRowHeight` for what happens when they are not.
      hasUniformRowHeight: true,
      // The cards opened by the user, by their place in the listing - see
      // `isVirtualized` for why they matter.
      expandedBlocks: {}
    }
  },

  computed: {
    blockComponent () {
      return this.config?.blockConfig?.component || null
    },

    blockConfig () {
      return this.config?.blockConfig || null
    },

    blocksRowAlign () {
      return this.config?.blocksRowAlign === true ? this.config?.blocksRowAlign : DEFAULT_BLOCKS_ROW_ALIGN
    },

    // What `v-row`'s `align` did, as the style it resolved to.
    rowAlignStyle () {
      return {
        alignItems: ROW_ALIGNMENTS[this.blocksRowAlign] || ROW_ALIGNMENTS[DEFAULT_BLOCKS_ROW_ALIGN]
      }
    },

    columnsPerRow () {
      // if sometimes would be necessary to override default number of columns per row, change here and provide value from config
      return this.isMobile ? DEFAULT_COLUMNS_PER_ROW_MOBILE : DEFAULT_COLUMNS_PER_ROW
    },

    totalRowsCount () {
      return Math.ceil(this.data.length / this.columnsPerRow)
    },

    /**
     * Whether this grid is drawn through a window - see VIRTUALIZE_FROM_ROWS.
     *
     * A window is only honest while something is there to say which rows are in it. A
     * grid that turns out not to sit in a scroll container of its own is drawn whole,
     * however long it is: better slow than a grid that stops after one screenful.
     */
    isVirtualized () {
      if (this.totalRowsCount <= VIRTUALIZE_FROM_ROWS || !this.hasUniformRowHeight || this.hasExpandedBlocks) {
        return false
      }

      return !this.isScrollContainerResolved || this.scrollContainer !== null
    },

    /**
     * Whether a card is open.
     *
     * An open card is taller than the others, which is the one thing a window cannot
     * take - the rows it is not drawing are counted at the height of a closed one. So
     * while any card is open the grid is drawn whole, and windowed again once they are
     * all closed. Nothing moves when it switches: every row above the window is a
     * closed one, so the spacer standing in for them was exactly their height.
     */
    hasExpandedBlocks () {
      return Object.keys(this.expandedBlocks).length > 0
    },

    rowHeight () {
      return this.measuredRowHeight || DEFAULT_ROW_HEIGHT
    },

    rowsOnScreen () {
      return Math.ceil(this.viewportHeight / this.rowHeight)
    },

    /**
     * How far past each edge of the window the rows are drawn anyway.
     *
     * A screenful either way. A scroll flung with the wheel or the scrollbar covers
     * ground faster than the cards can be redrawn, and a row that is not there yet is a
     * blank stripe where the grid should be.
     */
    rowOverscan () {
      return Math.max(MIN_ROW_OVERSCAN, this.rowsOnScreen)
    },

    renderStartRow () {
      if (!this.isVirtualized) {
        return 0
      }

      return Math.max(0, Math.floor(this.scrollTop / this.rowHeight) - this.rowOverscan)
    },

    renderEndRow () {
      if (!this.isVirtualized) {
        return this.totalRowsCount
      }

      return Math.min(this.totalRowsCount, this.renderStartRow + this.rowsOnScreen + this.rowOverscan * 2)
    },

    /**
     * The rows of cards drawn right now, each card with its place in the whole listing.
     *
     * The index travels beside the record rather than being written into a copy of it:
     * a block is handed the record itself, so it is the same object from one redraw to
     * the next and Vue can leave a card that has not changed alone - where a fresh copy
     * per render would redraw every card on screen on any change at all. Chunking is
     * done by arithmetic over the source array for the same reason: nothing outside the
     * window is touched, and while scrolling this runs every frame.
     */
    renderedRows () {
      const columnsPerRow = this.columnsPerRow
      const rows = []

      for (let rowIndex = this.renderStartRow; rowIndex < this.renderEndRow; rowIndex += 1) {
        const start = rowIndex * columnsPerRow
        const end = Math.min(start + columnsPerRow, this.data.length)
        const items = []

        for (let index = start; index < end; index += 1) {
          items.push({
            index,
            item: this.data[index]
          })
        }

        rows.push({
          index: rowIndex,
          items,
          // A last row the cards do not fill is not stretched to fill it - see
          // `.no-grow`.
          isShort: items.length < columnsPerRow
        })
      }

      return rows
    },

    topSpacerHeight () {
      return this.renderStartRow * this.rowHeight
    },

    bottomSpacerHeight () {
      return (this.totalRowsCount - this.renderEndRow) * this.rowHeight
    }
  },

  watch: {
    // A row holds one card on a phone and four on a desktop, so a screen crossing that
    // line is a grid of different rows - measured again rather than windowed on the
    // height the other layout had.
    columnsPerRow () {
      this.measuredRowHeight = 0
      this.hasUniformRowHeight = true
    },

    data: {
      handler (rows, previousRows) {
        // Vue runs a watcher whose value is an array even when the array handed back
        // is the one it already had, so a redraw of the grid would otherwise send the
        // window back to the top.
        if (rows === previousRows) {
          return
        }

        // A fresh set of records is read from the top, wherever the last one was left,
        // and is measured again - it is not the same grid any more.
        this.scrollTop = 0
        this.measuredRowHeight = 0
        this.hasUniformRowHeight = true
        this.expandedBlocks = {}

        if (this.scrollContainer) {
          this.scrollContainer.scrollTop = 0
        }

        this.$nextTick(this.measureViewport)
      }
    }
  },

  mounted () {
    this.attachScrollContainer()
    // Records already in hand at mount - a grid given its data rather than fetching it -
    // never reach `updated`, so the measuring has to be asked for here too.
    this.$nextTick(this.measureRowHeight)
  },

  updated () {
    this.measureRowHeight()
  },

  beforeDestroy () {
    this.detachScrollContainer()
  },

  methods: {
    onBlockExpansionChange (index, isExpanded) {
      if (isExpanded) {
        this.$set(this.expandedBlocks, index, true)
      }
      else {
        this.$delete(this.expandedBlocks, index)
      }
    },

    /**
     * Follows the scroll container the grid sits in, which is what says where the
     * window of drawn rows is. Everything here is a no-op on a grid small enough to be
     * drawn whole; only its scroll position is read, and only while it is windowed.
     */
    attachScrollContainer () {
      const container = this.findScrollContainer()

      this.isScrollContainerResolved = true

      if (!container) {
        return
      }

      this.scrollContainer = container
      container.addEventListener('scroll', this.onContainerScroll, { passive: true })

      if (typeof window.ResizeObserver === 'function') {
        this.resizeObserver = new window.ResizeObserver(this.measureViewport)
        this.resizeObserver.observe(container)
      }
      else {
        window.addEventListener('resize', this.measureViewport)
      }

      this.measureViewport()
    },

    /**
     * The element whose scroll position says which rows are on screen.
     *
     * Normally the wrapper the table view windows against too, so switching between the
     * two views does not change what is scrolling. A table given a fixed height keeps
     * that wrapper from scrolling and lets an ancestor do it instead, so when it is not
     * the one scrolling the first ancestor that is stands in for it - a container that
     * does not scroll would report nought however far the user had gone, and the grid
     * would stop after the first screenful.
     */
    findScrollContainer () {
      const wrapper = this.$el?.closest?.(SCROLL_CONTAINER_SELECTOR)

      if (wrapper && this.isScrollable(wrapper)) {
        return wrapper
      }

      let parent = (wrapper || this.$el)?.parentElement

      while (parent && parent !== document.body) {
        if (this.isScrollable(parent)) {
          return parent
        }

        parent = parent.parentElement
      }

      return null
    },

    isScrollable (element) {
      const { overflowY } = window.getComputedStyle(element)

      return SCROLLABLE_OVERFLOW.includes(overflowY)
    },

    detachScrollContainer () {
      if (this.scrollFrameId !== null) {
        window.cancelAnimationFrame(this.scrollFrameId)
        this.scrollFrameId = null
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      else {
        window.removeEventListener('resize', this.measureViewport)
      }

      this.scrollContainer?.removeEventListener('scroll', this.onContainerScroll)
      this.scrollContainer = null
    },

    // One reading per frame: a scroll fires far more often than the screen is drawn,
    // and every reading of it moves the window and so redraws the cards.
    onContainerScroll () {
      if (!this.isVirtualized || this.scrollFrameId !== null) {
        return
      }

      this.scrollFrameId = window.requestAnimationFrame(() => {
        this.scrollFrameId = null
        this.scrollTop = this.scrollContainer?.scrollTop ?? 0
      })
    },

    // Falls back to the browser window: a container measuring nothing is one that has
    // not been laid out yet, and a window of no rows would leave the grid empty.
    measureViewport () {
      this.viewportHeight = this.scrollContainer?.clientHeight || window.innerHeight
    },

    /**
     * Takes the row height from rows that have been drawn, so the window is measured in
     * the rows this grid actually has rather than in an assumed height.
     *
     * Measured from the top of one row to the top of the next rather than from a row's
     * own height: the gap between rows is a margin, and a window that left it out would
     * drift further from where the user is looking with every row it skipped.
     */
    measureRowHeight () {
      // Once is enough, and once is all it can afford: this runs after every redraw,
      // which while scrolling is every frame, and reading a height off the page makes
      // the browser lay it out there and then.
      if (!this.isVirtualized || this.measuredRowHeight) {
        return
      }

      const rows = this.$el?.querySelectorAll?.('.grid-view-row')

      if (!rows || rows.length < 2) {
        return
      }

      const firstPitch = rows[1].getBoundingClientRect().top - rows[0].getBoundingClientRect().top

      if (firstPitch <= 0) {
        return
      }

      // A window stands on one assumption: that a row it is not drawing is as tall as
      // one it is. A grid whose cards grow with their content breaks it, and nothing
      // measured in rows would land where the user is looking. So the rows on screen
      // are checked against each other, and a grid that fails is drawn whole: slower,
      // but it is the one that is right.
      const sampleSize = Math.min(rows.length, ROW_HEIGHT_SAMPLE)

      for (let index = 2; index < sampleSize; index += 1) {
        const pitch = rows[index].getBoundingClientRect().top - rows[index - 1].getBoundingClientRect().top

        if (Math.abs(pitch - firstPitch) > ROW_HEIGHT_TOLERANCE) {
          this.hasUniformRowHeight = false

          return
        }
      }

      this.measuredRowHeight = firstPitch
    }
  }
}
</script>

<style scoped>
/* The gap on the right is what keeps the cards off the scrollbar of the box they scroll
   in: without it a card ends exactly where the bar begins, and its border reads as part
   of the bar rather than as the edge of the card. */
.grid-view {
  width: 100%;
  height: 100%;
  padding-right: 8px;
  box-sizing: border-box;
}

.grid-view-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  /* Flush left: the first card in a row starts where the toolbar above the grid does. */
  margin: 0 0 10px 0;
}

.grid-view-row:last-child {
  margin-bottom: 0;
}

/* The rows above and below the window. Nothing but height - they carry no cards and
   must not add any spacing of their own to what they stand in for. */
.grid-view-spacer {
  margin: 0;
  padding: 0;
}

.grid-view-column {
  position: relative;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  padding: 0;
  flex: 1 1 calc(25% - 10px); /* ✅ Adjusted width to account for 10px gap */
  max-width: calc(25% - 10px);
}

.no-grow {
  flex-grow: 0 !important;
  max-width: 24%;
  flex-basis: 24%;
}

.grid-view-no-data {
  width: 100%;
  height: 100%;
  text-align: center;
  align-content: center;
  font-family: 'Satoshi', sans-serif;
  font-weight: 400;
  font-size: 16px;
  letter-spacing: 0;
  color: #111827;
}

@media screen and (max-width: 960px) {
  .grid-view-row {
    gap: 0;
    margin: 0 0 10px 0;
  }

  .grid-view-column {
    flex: 1 1 100% !important;
    max-width: 100% !important;
    width: 100%;
    margin-right: 0;
  }
}
</style>
